/**
 * Estado remoto del flujo publico, con TanStack Query.
 *
 * Tres operaciones: leer la disponibilidad, autocompletar una ficha y crear la reserva.
 *
 * ## Idempotencia (ADR-024)
 *
 * `useCrearReserva` administra la clave: la genera en el **primer** envio de una
 * intencion y la conserva mientras esa intencion siga viva. Un reintento por fallo de
 * red manda la misma clave, de modo que si la reserva ya se habia creado el servidor
 * devuelve la misma y no cobra dos veces. `reiniciarIntento()` la descarta, y hay que
 * llamarlo cuando la persona cambia de turno o empieza otra reserva.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

import type {
  ConsultaClientePublico,
  Disponibilidad,
  ReservaCreada,
  ResultadoBusquedaClientePublico,
  SolicitudReservaPublica,
} from '@/infra/contratos';
import type { ErrorPublico } from '@/infra/errores';
import { nuevaClaveIdempotencia } from '@/infra/idempotencia';
import { usePuerto } from '@/publico/estado/puerto';

/** Clave de cache de la disponibilidad de un dia local. */
export function claveDisponibilidad(fecha: string): readonly [string, string] {
  return ['disponibilidad', fecha];
}

/** Disponibilidad de las dos canchas para un dia local. */
export function useDisponibilidad(fecha: string): UseQueryResult<Disponibilidad, ErrorPublico> {
  const puerto = usePuerto();

  return useQuery<Disponibilidad, ErrorPublico>({
    queryKey: claveDisponibilidad(fecha),
    queryFn: () =>
      puerto.consultarDisponibilidad({ desdeLocalFecha: fecha, hastaLocalFecha: fecha }),
  });
}

/** Consulta puntual para mostrar enmascarada una cédula ya registrada (ADR-066). */
export function useBuscarClientePublico(): UseMutationResult<
  ResultadoBusquedaClientePublico,
  ErrorPublico,
  ConsultaClientePublico
> {
  const puerto = usePuerto();
  return useMutation<ResultadoBusquedaClientePublico, ErrorPublico, ConsultaClientePublico>({
    mutationFn: (consulta) => puerto.buscarClientePublico(consulta),
    retry: false,
  });
}

/** Datos de la reserva sin la clave de idempotencia, que la pone el hook. */
export type SolicitudSinClave = Omit<SolicitudReservaPublica, 'claveIdempotencia'>;

/** Resultado de `useCrearReserva`, con el control del ciclo de vida de la clave. */
export interface IntentoDeReserva {
  readonly mutacion: UseMutationResult<ReservaCreada, ErrorPublico, SolicitudSinClave>;
  /** Descarta la clave actual. Se llama al cambiar de turno o al empezar otra reserva. */
  readonly reiniciarIntento: () => void;
}

/**
 * Crea la reserva contra `crearReservaPublica`.
 *
 * Al terminar bien invalida la disponibilidad del dia: el turno recien tomado tiene que
 * desaparecer de la grilla para quien siga mirando la pantalla.
 */
export function useCrearReserva(fecha: string): IntentoDeReserva {
  const puerto = usePuerto();
  const cliente = useQueryClient();
  const clave = useRef<string | null>(null);

  const mutacion = useMutation<ReservaCreada, ErrorPublico, SolicitudSinClave>({
    mutationFn: (solicitud: SolicitudSinClave) => {
      clave.current ??= nuevaClaveIdempotencia();
      return puerto.crearReservaPublica({ ...solicitud, claveIdempotencia: clave.current });
    },
    // Un fallo de red se reintenta a mano, con un boton, para que la persona vea que
    // paso. Reintentar solo y en silencio esconde el estado real de la operacion.
    retry: false,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claveDisponibilidad(fecha) });
    },
    onError: (error: ErrorPublico) => {
      if (error.conflicto) {
        void cliente.invalidateQueries({ queryKey: claveDisponibilidad(fecha) });
      }
    },
  });

  const reiniciarIntento = useCallback(() => {
    clave.current = null;
  }, []);

  return { mutacion, reiniciarIntento };
}
